import type { ReactNode } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { FractionChain } from "@/components/tools/chemistry/fraction-chain";
import type { DIMENSIONAL_ANALYSIS_CALCULATOR_STRINGS } from "@/components/tools/dimensional-analysis-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Block,
  type FactorSpec,
  formatUnits,
  runChain,
  sameUnits,
  type UnitEntry,
} from "@/lib/tool-kit/chemistry/dimensional";
import {
  formatExact,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";

type Strings = typeof DIMENSIONAL_ANALYSIS_CALCULATOR_STRINGS;

function TargetCheck({
  target,
  remaining,
  strings,
}: {
  target: UnitEntry[] | null;
  remaining: UnitEntry[];
  strings: Strings;
}): ReactNode {
  if (target === null) {
    return null;
  }
  if (sameUnits(target, remaining)) {
    return (
      <Notice>{t(strings.targetOk, { target: formatUnits(target) })}</Notice>
    );
  }
  return (
    <ErrorNotice>
      {t(strings.targetMismatch, {
        units: formatUnits(remaining) || "—",
        target: formatUnits(target),
      })}
    </ErrorNotice>
  );
}

function halfText(
  fmt: NumberFormatter,
  value: number | null,
  tokens: readonly { unit: string; exp: number }[]
): string {
  const number = value === null ? "" : formatExact(fmt, value);
  const units = formatUnits(tokens.map(({ unit, exp }) => ({ unit, exp })));
  return `${number} ${units}`.trim();
}

/** The chain as one line of text: 60 mi/h × (1609.344 m / 1 mi) × … = 26.8 m/s */
function chainText(
  fmt: NumberFormatter,
  blocks: readonly Block[],
  answer: string
): string {
  const parts = blocks.map((block, index) => {
    const top = halfText(fmt, block.numerator.value, block.numerator.tokens);
    const bottom = halfText(
      fmt,
      block.denominator.value,
      block.denominator.tokens
    );
    if (index === 0) {
      return block.denominator.tokens.length > 0 ? `${top} / ${bottom}` : top;
    }
    return `(${top} / ${bottom})`;
  });
  return `${parts.join(" × ")} = ${answer}`;
}

export function ChainResults({
  strings,
  startValue,
  startUnits,
  target,
  specs,
  sig,
}: {
  strings: Strings;
  startValue: number;
  startUnits: UnitEntry[];
  target: UnitEntry[] | null;
  specs: FactorSpec[];
  sig: number;
}): ReactNode {
  const fmt = useToolNumber();
  const chain = runChain(startValue, startUnits, specs);
  if (!chain.ok) {
    const key = chain.error.code === "badFactor" ? "badFactor" : "noCommonUnit";
    return (
      <ErrorNotice>
        {t(strings[key], { number: fmt(chain.error.row + 1) })}
      </ErrorNotice>
    );
  }
  const unitsText = formatUnits(chain.remaining);
  const answer = `${formatSig(fmt, chain.value, sig)} ${unitsText}`.trim();
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.answer} value={answer} />
        <StatCard label={strings.unitsOfAnswer} value={unitsText || "—"} />
      </StatGrid>
      <FractionChain
        blocks={chain.blocks}
        cancelledLabel={strings.cancelled}
        fmt={fmt}
        result={answer}
      />
      <p className="text-muted-foreground text-xs">
        {t(strings.unrounded, { value: formatExact(fmt, chain.value) })}
      </p>
      <TargetCheck
        remaining={chain.remaining}
        strings={strings}
        target={target}
      />
      <p className="text-muted-foreground text-xs">{strings.exactNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={chainText(fmt, chain.blocks, answer)}
      />
    </>
  );
}
